import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { favorite, obj, TAllWishlist } from '../../types/wishlist'; // Adjust the import path as needed
import { wishlistServices } from '@/src/app/[locale]/account/wishlist/services/wishlist';
import { revalidateWishlist } from '@/src/lib/actions';

interface WishListState {
  myWishlist: TAllWishlist[];
  accountWishlist: favorite[];
  loading: boolean;
  setAccountWishlist: (update: favorite[] | ((prev: favorite[]) => favorite[])) => void;
  setWishlist: (wishlist: TAllWishlist[]) => void;
  isFavorite: (itemId: string, branchId: string | number) => boolean;
  toggleFavorite: (data: obj) => Promise<void>;
  forceUpdate: () => void;
  deleteWishlistFormAccount: (itemId: string, branchId: string | number) => void;
}

const useWishListStore = create<WishListState>()(
  devtools(
    persist(
      (set, get) => ({
        forceUpdate: () => set((state) => ({ ...state })), // Add forceUpdate function
        myWishlist: [],
        accountWishlist: [],
        setAccountWishlist: (update) => {
          set((state) => ({
            accountWishlist: typeof update === 'function' ? update(state.accountWishlist) : update,
          }));
        },
        loading: true,
        setWishlist: (wishlist: TAllWishlist[]) => set({ myWishlist: wishlist, loading: false }),
        isFavorite: (itemId: string, branchId: string | number) => {
          if (!itemId && !branchId) return false;
          return get().myWishlist.some(
            (item) =>
              String(item?.item_id) === String(itemId) &&
              Number(item?.branch_id) === Number(branchId),
          );
        },
        toggleFavorite: async (data: obj) => {
          const updatedWishlist = await wishlistServices.AddOrDelete(data);

          if (updatedWishlist) {
            set(() => ({ myWishlist: updatedWishlist }));
          }

          const isStillInWishlist = updatedWishlist?.some(
            (item: any) =>
              String(item?.item_id) === String(data.item_id) &&
              Number(item?.branch_id) === Number(data.branch_id),
          );

          if (!isStillInWishlist) {
            set((state) => ({
              accountWishlist: state.accountWishlist.filter(
                (item) =>
                  String(item?.itemId) !== String(data.item_id) ||
                  Number(item?.branchId) !== Number(data.branch_id),
              ),
            }));
          } else {
            wishlistServices
              .getWishlist({ page: '1', pageCount: '40' })
              .then((freshList) => {
                if (freshList && freshList.length > 0) {
                  set(() => ({ accountWishlist: freshList }));
                }
              })
              .catch(() => {});
          }

          revalidateWishlist().catch(() => {});
        },
        deleteWishlistFormAccount: async (itemId: string, branchId: string | number) => {
          const updatedWishlist = get().accountWishlist.filter(
            (item) =>
              String(item?.itemId) !== String(itemId) ||
              Number(item?.branchId) !== Number(branchId),
          );

          if (updatedWishlist) {
            set(() => ({ accountWishlist: updatedWishlist }));
          }
        },
      }),
      {
        name: 'wishlist-storage', // Unique name for the storage
      },
    ),
  ),
);

export default useWishListStore;
